import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ProductForm from './ProductForm';

interface PricePoint {
  id?: number;
  kartra_price_point: string;
  billing_cycle: string;
  billing_interval: number;
  billing_interval_unit: string;
  price: string;
  currency: string;
  base_monthly_equivalent: string;
  savings_percentage: string;
  display_name: string;
  is_default: boolean;
  is_active: boolean;
}

interface Product {
  id: number;
  kartra_product_id: string;
  kartra_product_name: string;
  product_slug: string;
  display_name: string;
  tagline: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  is_featured: boolean;
  is_popular: boolean;
  created_at: string;
  updated_at: string;
  price_points: PricePoint[];
  features: any[];
  limits: any[];
}

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/admin/products', {
        headers: {
          'X-Tenant-ID': '1'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.data || []);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleCreate = () => {
    setSelectedProduct(null);
    setShowForm(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setShowForm(true);
  };

  const handleDelete = async (product: Product) => {
    if (!confirm(`Are you sure you want to delete ${product.display_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiFetch(`/api/admin/products/${product.id}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-ID': '1'
        }
      });

      if (response.ok) {
        toast.success('Product deleted successfully');
        fetchProducts();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    }
  };

  const handleSyncKartra = async () => {
    if (!confirm('This will sync all Kartra transactions and subscriptions to products. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      const response = await apiFetch('/api/admin/products/sync-kartra', {
        method: 'POST',
        headers: {
          'X-Tenant-ID': '1'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Synced ${data.data.transactions} transactions and ${data.data.subscriptions} subscriptions`);
      } else {
        toast.error('Failed to sync Kartra data');
      }
    } catch (error) {
      console.error('Error syncing Kartra:', error);
      toast.error('Error syncing Kartra data');
    } finally {
      setSyncing(false);
    }
  };

  const handleFormClose = (shouldRefresh: boolean) => {
    setShowForm(false);
    setSelectedProduct(null);
    if (shouldRefresh) {
      fetchProducts();
    }
  };

  const getMonthlyPrice = (product: Product): string => {
    const monthlyPrice = product.price_points.find(pp => pp.billing_cycle === 'monthly');
    return monthlyPrice ? `$${monthlyPrice.price}` : 'N/A';
  };

  const getAnnualPrice = (product: Product): string => {
    const annualPrice = product.price_points.find(pp => pp.billing_cycle === 'annual');
    return annualPrice ? `$${annualPrice.price}` : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (showForm) {
    return <ProductForm product={selectedProduct} onClose={handleFormClose} />;
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-slate-400">
            Manage product pricing, features, and limits
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSyncKartra}
            disabled={syncing}
            className="inline-flex items-center px-4 py-2 border border-indigo-500/30 rounded-xl shadow-sm text-sm font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <ArrowPathIcon className={`-ml-1 mr-2 h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Kartra'}
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Product
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Kartra ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Monthly
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Annual
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Features
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Badges
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="text-slate-400">
                      <CubeIcon className="mx-auto h-12 w-12 mb-2 opacity-50" />
                      <p>No products found</p>
                      <p className="text-sm mt-1">Click "Add Product" to create your first product</p>
                    </div>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-700/30 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white">{product.display_name}</div>
                        <div className="text-sm text-slate-400">{product.tagline}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-300">{product.kartra_product_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-emerald-400">{getMonthlyPrice(product)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-emerald-400">{getAnnualPrice(product)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-300">{product.features?.length || 0} features</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {product.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                          <XCircleIcon className="w-4 h-4 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-1">
                        {product.is_popular && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            Popular
                          </span>
                        )}
                        {product.is_featured && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
                          title="Edit product"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-150"
                          title="Delete product"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
          <div className="text-sm text-slate-400">Total Products</div>
          <div className="text-2xl font-bold text-white mt-1">{products.length}</div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
          <div className="text-sm text-slate-400">Active Products</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1">
            {products.filter(p => p.is_active).length}
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
          <div className="text-sm text-slate-400">Popular Products</div>
          <div className="text-2xl font-bold text-yellow-400 mt-1">
            {products.filter(p => p.is_popular).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
